/**
 * Slices (owner's grapefruit model, 2026-10-06; scratchpad fruit-map reports §4 and the reconciled eating order D4): a slice is one
 * whole job a person eats completely: one entry, a few guided steps, the value, a reward screen with real numbers, then ONE offer of
 * the next slice. This file holds the slice ids, the eating order per person, the one next offer, the reward words and the slice
 * events (started → finished → rewarded → next_started).
 *
 * | Rule | How it is kept |
 * |---|---|
 * | S1 One next offer, never a menu | `nextSliceOffer` returns one offer or null; screens render it with "Not now" |
 * | S2 The eating order is the reconciled one | owner: Is this you? → first label → first reading → open for food → add one person → binder → a room → … |
 * | S3 Safety is never sequenced | check-in, report, allergy order and recall are always one tap away; they are never "next" behind something |
 * | S4 Rewards print only what is true | every reward line takes the real counts; nothing is invented when a count is missing |
 * | S5 Events carry no person and no health | `sliceEventProblem` accepts { slice, event } only (plus the org from the URL); anything else is refused |
 */

export const SLICE_IDS = [
  "is_this_you", "first_label", "first_reading", "open_for_food", "add_person", "first_binder", "clear_room", "inspected_now",
  "cool_pot", "signs", "allergen_menu", "whole_place", "check_in", "home_first_step",
] as const;
export type SliceId = (typeof SLICE_IDS)[number];
export const isSliceId = (v: unknown): v is SliceId => typeof v === "string" && (SLICE_IDS as readonly string[]).includes(v);

export const SLICE_EVENTS = ["started", "finished", "rewarded", "next_started"] as const;
export type SliceEvent = (typeof SLICE_EVENTS)[number];
export const isSliceEvent = (v: unknown): v is SliceEvent => typeof v === "string" && (SLICE_EVENTS as readonly string[]).includes(v);

export type SliceMoment = "glance" | "sit_down";
export interface SliceDef {
  id: SliceId;
  /** The offer's button: a verb and the thing. */
  offer: string;
  /** How long, in the person's terms. */
  effort: string;
  /** What they get, one line. */
  why: string;
  /** Web route; the phone maps it (logic/ahead.ts phoneHref). */
  to: string;
  moment: SliceMoment;
}

export const SLICES: Readonly<Record<SliceId, SliceDef>> = {
  is_this_you: { id: "is_this_you", offer: "Find your last inspection", effort: "20 seconds", why: "What the inspector saw, and 3 things to keep ahead of.", to: "/", moment: "sit_down" },
  first_label: { id: "first_label", offer: "Label your first food", effort: "10 seconds", why: "Know the day it goes, without counting.", to: "/food/label", moment: "glance" },
  first_reading: { id: "first_reading", offer: "Log the fridge temperature", effort: "20 seconds", why: "Your first record, and your morning list starts.", to: "/do?log=cold_units", moment: "glance" },
  open_for_food: { id: "open_for_food", offer: "Finish the opening checks", effort: "about 2 minutes", why: "Then it says: yes, you're open for food.", to: "/do", moment: "glance" },
  add_person: { id: "add_person", offer: "Add one person", effort: "2 minutes", why: "So it isn't all on you.", to: "/people/add", moment: "sit_down" },
  first_binder: { id: "first_binder", offer: "See your binder", effort: "1 minute", why: "Your records, the way an inspector reads them.", to: "/prove", moment: "sit_down" },
  clear_room: { id: "clear_room", offer: "Clear one room", effort: "about 5 minutes", why: "A room you can show anyone, on today's cleaning log.", to: "/rooms", moment: "sit_down" },
  inspected_now: { id: "inspected_now", offer: "See where you'd stand today", effort: "1 minute", why: "If an inspector came now: what's ready, and the one thing that needs you.", to: "/health", moment: "sit_down" },
  cool_pot: { id: "cool_pot", offer: "Cool one pot safely", effort: "20 seconds to start", why: "The timers remember the 2-hour and 6-hour readings for you.", to: "/food/cooling", moment: "glance" },
  signs: { id: "signs", offer: "Print your signs", effort: "2 minutes", why: "The signs your place must post, ready to print.", to: "/signs", moment: "sit_down" },
  allergen_menu: { id: "allergen_menu", offer: "Make your allergen menu", effort: "a few minutes", why: "Answer \"is there sesame?\" in one tap.", to: "/allergens/setup", moment: "sit_down" },
  whole_place: { id: "whole_place", offer: "Clean the whole place", effort: "about 40 minutes", why: "Every room, in order, ready to open.", to: "/clean", moment: "sit_down" },
  check_in: { id: "check_in", offer: "Do today's check-in", effort: "30 seconds", why: "Then you're cleared to work today.", to: "/check-in", moment: "glance" },
  home_first_step: { id: "home_first_step", offer: "Do your first step", effort: "a few minutes", why: "The fridge first: food that stays safe.", to: "/", moment: "glance" },
};

export type SliceWho = "owner" | "staff" | "home";
/** S2: the reconciled eating order (fruit-map reports, debate D4). Safety slices (check-in, report) are never in it for owners (S3). */
export const EATING_ORDER: Readonly<Record<SliceWho, readonly SliceId[]>> = {
  owner: ["is_this_you", "first_label", "first_reading", "open_for_food", "add_person", "first_binder", "clear_room", "inspected_now", "cool_pot", "signs", "allergen_menu", "whole_place"],
  staff: ["check_in", "first_reading", "first_label", "clear_room"],
  home: ["home_first_step", "first_label"],
};

export interface NextOffer extends SliceDef { /** Shown under the button. */ line: string }
export interface NextContext {
  who: SliceWho;
  /** Slices this person or place has already eaten (from the records: a label exists, a reading exists, 2+ people, …). */
  done?: readonly SliceId[];
  /** Slices that don't fit this place or role (no cooking → no cooling; staff can't add people; …). */
  skip?: readonly SliceId[];
}

/**
 * S1: the ONE next slice after `after`, in this person's eating order, skipping what's done or doesn't fit. Null when nothing is left
 * (then the screen offers no next slice, only its own buttons). A slice not in the order offers the order's first open slice.
 */
export function nextSliceOffer(after: SliceId, c: NextContext): NextOffer | null {
  const order = EATING_ORDER[c.who];
  const gone = new Set<SliceId>([after, ...(c.done ?? []), ...(c.skip ?? [])]);
  const at = order.indexOf(after);
  const rest = at >= 0 ? order.slice(at + 1) : order;
  const id = rest.find((x) => !gone.has(x));
  if (!id) return null;
  const d = SLICES[id];
  return { ...d, line: `${d.effort.charAt(0).toUpperCase()}${d.effort.slice(1)}. ${d.why}` };
}

/* ─────────────── the reward words (S4) ─────────────── */

export interface Reward { title: string; lines: string[] }

/**
 * A check or reading just saved (web Do, phone checks): the value and where it went, the opening list's count when the check is on
 * it, and "Yes, you're open for food" once that list is done. `value` is the reading as typed ("38°F"); none for a yes/no check.
 */
export function checkReward(o: { what: string; value?: string; opening?: { done: number; total: number } | null }): Reward & { openForFood: boolean } {
  const title = o.value ? `${o.what}: ${o.value} ✓` : `${o.what} ✓`;
  const lines = ["Saved to today's records."];
  const open = !!o.opening && o.opening.total > 0 && o.opening.done >= o.opening.total;
  if (o.opening && o.opening.total > 0) lines.push(open ? `Opening checks: ${o.opening.total} of ${o.opening.total} done.` : `Opening checks: ${o.opening.done} of ${o.opening.total} done.`);
  return { title, lines, openForFood: open };
}
export const OPEN_FOR_FOOD = "Yes, you're open for food";

/** The reading a check saved, as the person typed it ("38°F", "200 ppm"): the first °F or ppm column with a value. None for yes/no checks. */
export function readingOf(columns: readonly { key: string; label: string }[], values: Readonly<Record<string, unknown>>): string | undefined {
  for (const c of columns) {
    const v = values[c.key];
    if (v === undefined || v === null || v === "") continue;
    // Typed with its unit (core temperature.ts): "6°C" as typed.
    if (/°[CF]$/.test(String(v))) return String(v);
    // "ppm or °F" (the sanitizer log): hot water is a temperature, every chemical a concentration.
    if (/°F/.test(c.label) && /\bppm\b/.test(c.label)) return /hot water/i.test(String(values.kind ?? "")) ? `${v}°F` : `${v} ppm`;
    if (/°F/.test(c.label)) return `${v}°F`;
    if (/\bppm\b/.test(c.label)) return `${v} ppm`;
  }
  return undefined;
}

/** The opening list's count from the day's expected checks (core schedule.ts openForFood: the blocking ones). */
export function openingCount(expected: readonly { blocking?: boolean; satisfied?: boolean }[]): { done: number; total: number } {
  const list = expected.filter((e) => e.blocking);
  return { done: list.filter((e) => e.satisfied).length, total: list.length };
}

/** "page" / "pages", "record" / "records". */
const n = (k: number, one: string, many = `${one}s`) => `${k} ${k === 1 ? one : many}`;

/**
 * The binder just built (web Prove): its real page count (read from the PDF) and record count, and what it proves: how many of the
 * Food Code rules for this place have a record in the range. No health detail: check-ins count as records, never by answer (T4).
 */
export function binderReward(o: { pages: number; records: number; rulesWithRecords: number; rulesTotal: number; range: string }): Reward {
  return {
    title: `Your binder: ${n(o.pages, "page")}, ${n(o.records, "record")} ✓`,
    lines: [
      o.rulesTotal > 0 ? `It shows ${o.rulesWithRecords} of ${o.rulesTotal} Food Code rules with records, ${o.range}.` : `Your records, ${o.range}.`,
      "Hand it to an inspector, or keep it for your own look.",
    ],
  };
}

/** Pages in a PDF the API built: "/Type /Page" objects, not the "/Pages" tree. 0 when the bytes aren't a PDF. */
export function pdfPageCount(bytes: Uint8Array): number {
  const head = String.fromCharCode(...bytes.subarray(0, 5));
  if (head !== "%PDF-") return 0;
  let text = "";
  for (let i = 0; i < bytes.length; i += 8192) text += String.fromCharCode(...bytes.subarray(i, Math.min(i + 8192, bytes.length)));
  return (text.match(/\/Type\s*\/Page(?![a-zA-Z])/g) ?? []).length;
}

/** The whole place just finished (web CleanPlace, phone clean): rooms and steps from the run, the time and who. */
export function wholePlaceReward(o: { title: string; rooms: number; time?: string; by?: string }): Reward {
  return {
    title: o.title,
    lines: [`${n(o.rooms, "room")} clear, every one checked since this clean began.`, ...(o.time ? [`On today's cleaning log at ${o.time}${o.by ? `, by ${o.by}` : ""}.`] : [])],
  };
}

/**
 * The inviter's fruit (fruit-map gap 4): someone they added finished joining. "Set up" = joined the team (accepted the invite, or a
 * tablet PIN was made). It never says anything about a check-in or health (T4): "ready" here is joined, nothing more.
 */
export function inviteeSetUpLine(o: { first: string; setUp: number; total: number }): string {
  return `${o.first} is set up. ${o.setUp} of ${o.total} on your team are set up.`;
}

/* ─────────────── slice events (S5) ─────────────── */

export interface SliceEventBody { slice: SliceId; event: SliceEvent }

/** The one shape the server accepts. Any other field (a name, an email, a reading, an answer) is refused, not dropped. */
export function sliceEventProblem(b: unknown): string | null {
  if (!b || typeof b !== "object" || Array.isArray(b)) return "Send { slice, event }.";
  const o = b as Record<string, unknown>;
  const extra = Object.keys(o).filter((k) => k !== "slice" && k !== "event");
  if (extra.length) return `Only slice and event are kept; remove ${extra.join(", ")}.`;
  if (!isSliceId(o.slice)) return '"slice" must be a slice id.';
  if (!isSliceEvent(o.event)) return '"event" must be started, finished, rewarded or next_started.';
  return null;
}

/** One counter row: a Utah day, a slice, an event, how many. No person, no org name, no content. */
export interface SliceCount { day: string; slice: SliceId; event: SliceEvent; n: number }
